import { useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/Button";

// Prevent browser from restoring scroll position on this page
window.history.scrollRestoration = "manual";

export const PrivacyPolicy = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = location.state?.from;
  const shouldShowBackButton = Boolean(backTo);

  useLayoutEffect(() => {
    const rootContainer = document.getElementById("root");
    if (rootContainer) {
      rootContainer.scrollTop = 0;
    }
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {shouldShowBackButton && (
        <div className="mb-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(backTo)}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            className="text-primary-600 hover:text-primary-700 !px-0"
          >
            Back
          </Button>
        </div>
      )}

      <div className="prose max-w-none">
        <h1 className="text-3xl font-bold text-theme-text-heading mb-6">Privacy Policy</h1>

        <p className="text-sm text-theme-text-secondary mb-8">Last updated: May 12, 2026</p>

        <div className="space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">1. Introduction</h2>
            <p className="text-theme-text leading-relaxed">
              Forward Share Ventures ("we," "our," or "us") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you use our professional networking and mentorship platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              2. Information We Collect
            </h2>

            <h3 className="text-lg font-medium text-theme-text-heading mb-2">
              Personal Information
            </h3>
            <div className="text-theme-text leading-relaxed space-y-2 mb-4">
              <p>We may collect the following personal information:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name, email address, and contact information</li>
                <li>Professional background and experience</li>
                <li>Career goals and preferences</li>
                <li>Session preferences and availability</li>
                <li>Communication history within the platform</li>
              </ul>
            </div>

            <h3 className="text-lg font-medium text-theme-text-heading mb-2">Usage Information</h3>
            <div className="text-theme-text leading-relaxed space-y-2">
              <p>We automatically collect certain information when you use our platform:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Device information and browser type</li>
                <li>IP address and location data</li>
                <li>Platform usage patterns and preferences</li>
                <li>Session logs and interaction data</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              3. How We Use Your Information
            </h2>
            <div className="text-theme-text leading-relaxed space-y-2">
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide and maintain our platform services</li>
                <li>Match achievers with suitable advisors</li>
                <li>Facilitate communication and scheduling</li>
                <li>Improve our platform and user experience</li>
                <li>Send important notifications and updates</li>
                <li>Ensure platform security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              4. Information Sharing and Disclosure
            </h2>
            <div className="text-theme-text leading-relaxed space-y-2">
              <p>We may share your information in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <strong>With matched users:</strong> Basic profile information to facilitate
                  connections
                </li>
                <li>
                  <strong>Service providers:</strong> Third-party vendors who assist in platform
                  operations
                </li>
                <li>
                  <strong>Legal requirements:</strong> When required by law or to protect our rights
                </li>
                <li>
                  <strong>Business transfers:</strong> In connection with mergers, acquisitions, or
                  asset sales
                </li>
              </ul>
              <p className="mt-2">
                We do not sell, trade, or rent your personal information to third parties.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              4.1 Google API Data Disclosure
            </h2>
            <div className="text-theme-text leading-relaxed space-y-2">
              <p>
                Our app's use and transfer to any other app of information received from Google APIs
                will adhere to the{" "}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Google API Services User Data Policy
                </a>
                , including the Limited Use requirements.
              </p>
              <p>
                We do not use Google user data to serve advertisements or for any purpose other than
                providing and improving the AchieveOS platform features.
              </p>
              <p>
                We do not allow humans to read Google user data unless we have first obtained your
                affirmative agreement for specific messages, it is necessary for security purposes
                such as investigating abuse, to comply with applicable law, or for the platform's
                internal operations and even then only when the data have been aggregated and
                anonymized.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">5. Data Security</h2>
            <p className="text-theme-text leading-relaxed">
              We implement appropriate technical and organizational security measures to protect
              your personal information against unauthorized access, alteration, disclosure, or
              destruction. However, no method of transmission over the internet is 100% secure, and
              we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              6. Data Retention
            </h2>
            <p className="text-theme-text leading-relaxed">
              We retain your personal information for as long as necessary to provide our services
              and fulfill the purposes outlined in this Privacy Policy. We may retain certain
              information for longer periods as required by law or for legitimate business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              7. Your Rights and Choices
            </h2>
            <div className="text-theme-text leading-relaxed space-y-2">
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access and review your personal information</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Data portability (where applicable)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              8. Cookies and Tracking Technologies
            </h2>
            <p className="text-theme-text leading-relaxed">
              We use cookies and similar tracking technologies to enhance your experience on our
              platform. You can control cookie preferences through your browser settings, though
              some features may not function properly if cookies are disabled.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              9. International Data Transfers
            </h2>
            <p className="text-theme-text leading-relaxed">
              Your information may be transferred to and processed in countries other than your
              country of residence. We ensure appropriate safeguards are in place to protect your
              personal information in accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              10. Children's Privacy
            </h2>
            <p className="text-theme-text leading-relaxed">
              Our platform is not intended for individuals under the age of 18. We do not knowingly
              collect personal information from children under 18. If we discover that we have
              collected information from a child under 18, we will delete that information promptly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">
              11. Changes to This Privacy Policy
            </h2>
            <p className="text-theme-text leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new Privacy Policy on this page and updating the "Last
              updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-theme-text-heading mb-3">12. Contact Us</h2>
            <p className="text-theme-text leading-relaxed">
              If you have any questions about this Privacy Policy or our privacy practices, please
              contact us: hello@forwardachieve.co
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
